import { useState, useEffect } from 'react';
import { useTitle } from '../../context/TitleContext';
import { Card, CardBody, CardHeader, CardTitle, Row, Col, Spinner, Badge, Table } from 'react-bootstrap';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import apiService from '../../services/api.service';
import '../../styles/products.scss';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const FinanceReport = () => {
  const { setTitle } = useTitle();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    setTitle('Rapport Financier');
    fetchReportData();
  }, [setTitle]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get('/reports/finance');
      if (response.success) {
        setReportData(response.data);
      } else {
        setError('Erreur lors du chargement des données');
      }
    } catch (err) {
      console.error('Error fetching finance report:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-2">Chargement des données...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <h4 className="alert-heading">Erreur!</h4>
        <p>{error}</p>
        <hr />
        <button className="btn btn-outline-danger" onClick={fetchReportData}>
          Réessayer
        </button>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="alert alert-warning" role="alert">
        Aucune donnée disponible
      </div>
    );
  }

  const { monthlyData, categoryData, paymentData, cityData, summary, year } = reportData;

  const lineChartData = {
    labels: monthlyData.labels,
    datasets: [
      {
        label: 'Revenus (DH)',
        data: monthlyData.income,
        borderColor: '#00D4AA',
        backgroundColor: 'rgba(0, 212, 170, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const ordersChartData = {
    labels: monthlyData.labels,
    datasets: [
      {
        label: 'Nombre de commandes',
        data: monthlyData.orders,
        backgroundColor: '#4ECDC4',
        borderColor: '#26A69A',
        borderWidth: 1,
      },
    ],
  };

  const categoryChartData = {
    labels: categoryData.labels.length > 0 ? categoryData.labels : ['Aucune catégorie'],
    datasets: [
      {
        data: categoryData.values.length > 0 ? categoryData.values : [1],
        backgroundColor: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD'],
        borderColor: ['#FF5252', '#26A69A', '#2980B9', '#66BB6A', '#FDCB6E', '#BA68C8'],
        borderWidth: 2,
      },
    ],
  };

  const paymentChartData = {
    labels: paymentData.labels.length > 0 ? paymentData.labels : ['Aucune donnée'],
    datasets: [
      {
        data: paymentData.values.length > 0 ? paymentData.values : [1],
        backgroundColor: ['#4ECDC4', '#FF6B6B', '#45B7D1', '#96CEB4'],
        borderWidth: 2,
      },
    ],
  };

  const cityChartData = {
    labels: cityData.labels,
    datasets: [
      {
        label: 'Revenus par ville (DH)',
        data: cityData.values,
        backgroundColor: '#c5a05e',
        borderColor: '#a88b4a',
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            weight: '500',
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#00D4AA',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          callback: function(value) {
            return value.toLocaleString() + ' DH';
          },
          font: {
            size: 11,
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
    },
  };

  const ordersChartOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales.y,
        ticks: {
          callback: function(value) {
            return value;
          },
          font: {
            size: 11,
          },
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: '500',
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return context.label + ': ' + context.parsed.toLocaleString() + ' DH (' + percentage + '%)';
          },
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#00D4AA',
        borderWidth: 1,
        cornerRadius: 8,
      },
    },
  };

  return (
    <div className="finance-report-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">
          <Badge bg="secondary">Année {year}</Badge>
        </h5>
        <button className="btn btn-outline-primary btn-sm" onClick={fetchReportData}>
          <i className="solar:refresh-circle-bold-duotone me-1"></i>
          Actualiser
        </button>
      </div>

      <Row className="g-3 mb-4">
        <Col xl={3} lg={6} md={6}>
          <Card className="text-center h-100">
            <CardBody>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <i className="solar:chart-square-bold-duotone fs-2 text-primary me-2"></i>
                <h4 className="mb-0" style={{ color: '#c5a05e' }}>{summary.totalRevenue.toLocaleString()} DH</h4>
              </div>
              <p className="text-muted mb-0">Revenus Totaux</p>
              {summary.revenueGrowth !== 0 && (
                <Badge bg={summary.revenueGrowth > 0 ? 'success' : 'danger'} className="mt-2">
                  {summary.revenueGrowth > 0 ? '+' : ''}{summary.revenueGrowth}% ce mois
                </Badge>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col xl={3} lg={6} md={6}>
          <Card className="text-center h-100">
            <CardBody>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <i className="solar:cart-check-bold-duotone fs-2 text-success me-2"></i>
                <h4 className="mb-0" style={{ color: '#c5a05e' }}>{summary.totalOrders}</h4>
              </div>
              <p className="text-muted mb-0">Total Commandes</p>
              <small className="text-success">{summary.deliveredOrders} livrées</small>
            </CardBody>
          </Card>
        </Col>
        <Col xl={3} lg={6} md={6}>
          <Card className="text-center h-100">
            <CardBody>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <i className="solar:hourglass-bold-duotone fs-2 text-warning me-2"></i>
                <h4 className="mb-0" style={{ color: '#f59e0b' }}>{summary.pendingOrders}</h4>
              </div>
              <p className="text-muted mb-0">Commandes en Attente</p>
            </CardBody>
          </Card>
        </Col>
        <Col xl={3} lg={6} md={6}>
          <Card className="text-center h-100">
            <CardBody>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <i className="solar:tag-price-bold-duotone fs-2 text-info me-2"></i>
                <h4 className="mb-0" style={{ color: '#c5a05e' }}>{summary.avgOrderValue.toLocaleString()} DH</h4>
              </div>
              <p className="text-muted mb-0">Panier Moyen</p>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        <Col xl={8} lg={12}>
          <Card>
            <CardHeader>
              <CardTitle as="h6" className="mb-0">Évolution Mensuelle des Revenus ({year})</CardTitle>
            </CardHeader>
            <CardBody>
              <div style={{ height: '400px' }}>
                <Line data={lineChartData} options={chartOptions} />
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl={4} lg={12}>
          <Card>
            <CardHeader>
              <CardTitle as="h6" className="mb-0">Revenus par Catégorie</CardTitle>
            </CardHeader>
            <CardBody>
              <div style={{ height: '400px' }}>
                <Doughnut data={categoryChartData} options={doughnutOptions} />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-2">
        <Col xl={6} lg={12}>
          <Card>
            <CardHeader>
              <CardTitle as="h6" className="mb-0">Commandes par Mois</CardTitle>
            </CardHeader>
            <CardBody>
              <div style={{ height: '300px' }}>
                <Bar data={ordersChartData} options={ordersChartOptions} />
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl={6} lg={12}>
          <Card>
            <CardHeader>
              <CardTitle as="h6" className="mb-0">Méthodes de Paiement</CardTitle>
            </CardHeader>
            <CardBody>
              <div style={{ height: '300px' }}>
                <Doughnut data={paymentChartData} options={doughnutOptions} />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-2">
        <Col xl={12}>
          <Card>
            <CardHeader>
              <CardTitle as="h6" className="mb-0">Revenus par Ville</CardTitle>
            </CardHeader>
            <CardBody>
              <div style={{ height: '300px' }}>
                <Bar data={cityChartData} options={chartOptions} />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-2">
        <Col xl={6}>
          <Card>
            <CardHeader>
              <CardTitle as="h6" className="mb-0">Top Villes par Commandes</CardTitle>
            </CardHeader>
            <CardBody>
              <Table striped hover responsive size="sm">
                <thead>
                  <tr>
                    <th>Ville</th>
                    <th className="text-center">Commandes</th>
                    <th className="text-end">Total (DH)</th>
                  </tr>
                </thead>
                <tbody>
                  {cityData.labels.map((city, index) => (
                    <tr key={index}>
                      <td>{city}</td>
                      <td className="text-center">{cityData.counts[index]}</td>
                      <td className="text-end">{cityData.values[index].toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
        <Col xl={6}>
          <Card>
            <CardHeader>
              <CardTitle as="h6" className="mb-0">Détail des Paiements</CardTitle>
            </CardHeader>
            <CardBody>
              <Table striped hover responsive size="sm">
                <thead>
                  <tr>
                    <th>Méthode</th>
                    <th className="text-center">Transactions</th>
                    <th className="text-end">Montant (DH)</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentData.labels.map((method, index) => (
                    <tr key={index}>
                      <td>{method}</td>
                      <td className="text-center">{paymentData.counts[index]}</td>
                      <td className="text-end">{paymentData.values[index].toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default FinanceReport;